import { useCallback, useState } from 'react'
import { BookOpen, X } from 'lucide-react'
import useIsStandalone from 'hooks/useIsStandalone'
import FieldPrimerDialog from 'features/Help/FieldPrimerDialog'

/**
 * Offers the setup primer to somebody who has not set this phone up yet.
 *
 * Linear: MYD-459.
 *
 * ---------------------------------------------------------------------------
 * Not a fourth tutorial, and that is the point
 * ---------------------------------------------------------------------------
 *
 * Three first-run mechanisms already exist: `onboardingSlice`, the
 * `OnboardingFlow` wizard, and a driver.js tour of the desktop sidebar. The
 * issue asked which of them should own this. **None of them should.**
 *
 * All three are desktop wizards that run once per account. The primer is about
 * setting up a phone, which is per DEVICE and cannot be done from a desk.
 * Adding a seventh step to `OnboardingFlow` would gate completion on something
 * the user cannot do where they are standing, and a fourth independent flag
 * would mean a new user meeting several tutorials in an order nobody chose.
 *
 * So this is not a tutorial at all. It is a hint on the one screen where the
 * advice applies, on the device it applies to.
 *
 * ---------------------------------------------------------------------------
 * How it knows to stop
 * ---------------------------------------------------------------------------
 *
 * **Installed means done.** If the app is running standalone, the user has
 * already completed the central step the primer teaches, so there is nothing
 * to offer and the banner never renders. That is a fact about this device
 * rather than a guess, which is why it is the primary condition.
 *
 * **Dismissal is per device, deliberately.** "Set up this phone" is a
 * per-device task. Storing it against the account would mean setting up one
 * phone silences the offer on the next one, where it is exactly as useful. A
 * new device offering it again is correct behaviour, not a bug.
 *
 * It does NOT reuse the walkthrough's `mdt_field_primer_progress` key. That
 * one records how far through the reading somebody got, and a reader who
 * closed at step 3 has neither finished nor declined. Conflating the two would
 * either nag somebody who is halfway through or silence somebody who glanced
 * and left.
 */
const DISMISSED_KEY = 'mdt_primer_offer_dismissed'

/** Storage throws in a private window, and a hint is never worth a crash. */
const wasDismissed = (): boolean => {
	try {
		return window.localStorage.getItem(DISMISSED_KEY) === 'true'
	} catch {
		// Unreadable storage means an un-dismissable banner, which is worse than
		// a missing one: it would reappear on every load with no way to stop it.
		return true
	}
}

const rememberDismissed = () => {
	try {
		window.localStorage.setItem(DISMISSED_KEY, 'true')
	} catch {
		/* Dismissed for this session at least. */
	}
}

export function PrimerOfferBanner() {
	const isStandalone = useIsStandalone()
	const [dismissed, setDismissed] = useState(wasDismissed)
	const [primerOpen, setPrimerOpen] = useState(false)

	const dismiss = useCallback(() => {
		setDismissed(true)
		rememberDismissed()
	}, [])

	/*
		Opening the walkthrough counts as taking up the offer, so the banner
		goes for good. Somebody who has read it does not need asking again, and
		making them find the X as well would be asking twice.
	*/
	const open = useCallback(() => {
		setPrimerOpen(true)
		setDismissed(true)
		rememberDismissed()
	}, [])

	/*
		The BANNER hides; the dialog does not.

		An early return above the dialog would unmount it the moment "Show me
		how" was clicked, because taking up the offer also dismisses the banner.
		The walkthrough would open and vanish in the same tick.
	*/
	const showBanner = !isStandalone && !dismissed

	return (
		<>
			{showBanner && (
				<div className="mx-4 mt-4 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
					<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
						<BookOpen className="h-4 w-4" />
					</span>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-gray-900">Finish setting up this phone</p>
						{/*
						Names the two things it is actually for. "Take the tour" would
						not tell anybody why they should, and the precise-location step
						is the one that silently ruins every pin if it is skipped.
					*/}
						<p className="mt-0.5 text-sm leading-relaxed text-gray-600">
							Add MYDealTeams to your Home screen and turn on precise location, so the map puts you on the right corner.
						</p>
						<button type="button" onClick={open} className="mt-2 text-sm font-semibold text-primary hover:underline">
							Show me how
						</button>
					</div>
					{/*
					A 44px target around a small glyph, per the mobile guidance. Not
					the blue disc used for closing a SCREEN: this dismisses a hint,
					and borrowing that weight would overstate it.
				*/}
					<button
						type="button"
						onClick={dismiss}
						aria-label="Dismiss"
						className="-mr-2 -mt-2 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-400 hover:text-gray-600"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			)}

			<FieldPrimerDialog open={primerOpen} onClose={() => setPrimerOpen(false)} />
		</>
	)
}

export default PrimerOfferBanner
